import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  FiRefreshCw,
  FiPackage,
  FiAlertCircle,
  FiClipboard,
  FiTruck,
  FiShoppingBag,
} from "react-icons/fi";
import { baseurl } from "../../config/url";
import TrackingSidebar from "../ordertrackingcomponents/TrackingSidebar";
import OrderDetails from "../ordertrackingcomponents/OrderDetails";
import { showErrorToast } from "../../utils/toaster";

function Trackingpage() {
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [orders, setOrders] = useState([]);
  const [selectedproduct, setselectedproduct] = useState({});
  const [selecteditem, setselecteditem] = useState(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [isEmptyState, setIsEmptyState] = useState(false);

  const token =
    localStorage.getItem("sellerToken") ||
    localStorage.getItem("token") ||
    localStorage.getItem("adminToken");

  const handleSelectOrder = useCallback((orderId) => {
    setSelectedOrderId(orderId);
  }, []);

  const getorders = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      setIsEmptyState(false);

      if (!token) {
        const msg = "Authentication token not found. Please login again.";
        setOrders([]);
        setSelectedOrderId(null);
        setError(msg);
        showErrorToast(msg);
        return;
      }

      const response = await axios.get(`${baseurl}/api/order/seller-order`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        timeout: 20000,
      });

      const data = Array.isArray(response?.data) ? response.data : [];

      setOrders(data);

      if (data.length === 0) {
        setSelectedOrderId(null);
        setIsEmptyState(true);
        return;
      }

      setSelectedOrderId((prevId) => {
        const exists = data.some((item) => item._id === prevId);
        return exists ? prevId : data[0]._id;
      });
    } catch (error) {
      console.error(
        "Error fetching seller orders:",
        error?.response?.data || error.message
      );

      const status = error?.response?.status;
      const backendMessage = error?.response?.data?.message || "";

      if (
        status === 404 &&
        backendMessage.toLowerCase().includes("no orders found")
      ) {
        setOrders([]);
        setSelectedOrderId(null);
        setError("");
        setIsEmptyState(true);
        return;
      }

      let message = "Failed to fetch seller orders.";

      if (status === 401) {
        message = "Unauthorized. Please login again.";
      } else if (status === 403) {
        message = "Access denied. Seller login required.";
      } else if (error?.code === "ECONNABORTED") {
        message = "Request timeout. Please try again.";
      } else if (!error?.response) {
        message = "Server not reachable. Check API URL or CORS.";
      } else if (backendMessage) {
        message = backendMessage;
      }

      setOrders([]);
      setSelectedOrderId(null);
      setError(message);
      setIsEmptyState(false);
      showErrorToast(message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    getorders();
  }, [getorders]);

  const selectedOrder = useMemo(() => {
    return orders.find((order) => order._id === selectedOrderId) || null;
  }, [orders, selectedOrderId]);

  return (
    <div className="min-h-screen bg-slate-100 p-4 md:p-6">
      {/* Top Header */}
      <div className="mb-6 rounded-3xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-col gap-5 p-5 md:p-7 xl:flex-row xl:items-center xl:justify-between">
          <div className="min-w-0">
            <div className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700 border border-blue-100">
              <FiTruck size={14} />
              Seller Order Tracking
            </div>

            <h1 className="mt-3 text-2xl md:text-3xl font-bold tracking-tight text-slate-900">
              Order Tracking Dashboard
            </h1>

            <p className="mt-2 max-w-2xl text-sm md:text-base text-slate-500">
              Monitor seller orders, review live tracking progress, and manage
              delivery updates from one place.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <div className="min-w-[140px] rounded-2xl border border-blue-100 bg-blue-50 px-4 py-3">
              <div className="text-xs font-semibold uppercase tracking-wide text-blue-600">
                Total Orders
              </div>
              <div className="mt-1 text-2xl font-bold text-blue-800">
                {orders.length}
              </div>
            </div>

            <button
              onClick={getorders}
              disabled={loading}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-blue-600 px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <FiRefreshCw className={loading ? "animate-spin" : ""} size={16} />
              Refresh Orders
            </button>
          </div>
        </div>
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[380px_minmax(0,1fr)]">
        {/* Left Panel */}
        <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-200 bg-slate-50/70 px-5 py-4 md:px-6">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="text-lg font-bold text-slate-900">Orders List</h2>
                <p className="mt-1 text-sm text-slate-500">
                  Select any seller order to view tracking details and actions.
                </p>
              </div>

              <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-white border border-slate-200 text-slate-600 shadow-sm">
                <FiShoppingBag size={18} />
              </div>
            </div>
          </div>

          <div className="min-h-[520px]">
            {loading ? (
              <div className="flex h-full min-h-[520px] flex-col items-center justify-center p-8 text-center">
                <div className="h-12 w-12 animate-spin rounded-full border-4 border-slate-200 border-t-blue-600" />
                <h3 className="mt-4 text-base font-semibold text-slate-900">
                  Loading orders
                </h3>
                <p className="mt-1 text-sm text-slate-500">
                  Fetching seller order records from the server.
                </p>
              </div>
            ) : error ? (
              <div className="p-5 md:p-6">
                <div className="rounded-2xl border border-red-200 bg-red-50 p-5">
                  <div className="flex items-start gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white border border-red-100 text-red-600">
                      <FiAlertCircle size={18} />
                    </div>

                    <div>
                      <h3 className="text-base font-semibold text-red-700">
                        Unable to load orders
                      </h3>
                      <p className="mt-1 text-sm leading-6 text-red-600">
                        {error}
                      </p>
                    </div>
                  </div>
                </div>

                <button
                  onClick={getorders}
                  className="mt-4 w-full rounded-2xl bg-blue-600 px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700"
                >
                  Retry
                </button>
              </div>
            ) : isEmptyState ? (
              <div className="flex h-full min-h-[520px] flex-col items-center justify-center p-8 text-center">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-slate-100 text-slate-500">
                  <FiClipboard size={28} />
                </div>
                <h3 className="mt-4 text-lg font-semibold text-slate-900">
                  No orders yet
                </h3>
                <p className="mt-2 max-w-sm text-sm leading-6 text-slate-500">
                  No seller orders were found right now. Once customers place
                  orders for your products, they will appear here.
                </p>
              </div>
            ) : orders.length === 0 ? (
              <div className="flex h-full min-h-[520px] flex-col items-center justify-center p-8 text-center">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-slate-100 text-slate-500">
                  <FiPackage size={28} />
                </div>
                <h3 className="mt-4 text-lg font-semibold text-slate-900">
                  No orders found
                </h3>
                <p className="mt-2 max-w-sm text-sm leading-6 text-slate-500">
                  There are currently no seller orders available to display.
                </p>
              </div>
            ) : (
              <div className="max-h-[calc(100vh-240px)] overflow-y-auto">
                <TrackingSidebar
                  selectedId={selectedOrderId}
                  onSelect={handleSelectOrder}
                  trackingData={orders}
                />
              </div>
            )}
          </div>
        </div>

        {/* Right Panel */}
        <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-200 bg-slate-50/70 px-5 py-4 md:px-6">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="text-lg font-bold text-slate-900">
                  Order Details
                </h2>
                <p className="mt-1 text-sm text-slate-500">
                  Review tracking timeline, customer information, product items,
                  and delivery progress.
                </p>
              </div>

              <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-white border border-slate-200 text-slate-600 shadow-sm">
                <FiTruck size={18} />
              </div>
            </div>
          </div>

          <div className="p-4 md:p-6">
            {loading ? (
              <div className="flex min-h-[520px] flex-col items-center justify-center rounded-2xl border border-slate-200 bg-slate-50 p-10 text-center">
                <div className="h-12 w-12 animate-spin rounded-full border-4 border-slate-200 border-t-blue-600" />
                <h3 className="mt-4 text-base font-semibold text-slate-900">
                  Loading details
                </h3>
                <p className="mt-1 text-sm text-slate-500">
                  Preparing selected order tracking information.
                </p>
              </div>
            ) : selectedOrder ? (
              <OrderDetails
                order={selectedOrder}
                setselecteditem={setselecteditem}
                selecteditem={selecteditem}
                getorders={getorders}
                setselectedproduct={setselectedproduct}
                selectedproduct={selectedproduct}
              />
            ) : (
              <div className="flex min-h-[520px] flex-col items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-10 text-center">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-white border border-slate-200 text-slate-500 shadow-sm">
                  <FiPackage size={28} />
                </div>
                <h3 className="mt-4 text-lg font-semibold text-slate-900">
                  Select an order
                </h3>
                <p className="mt-2 max-w-md text-sm leading-6 text-slate-500">
                  Choose an order from the left panel to see its tracking
                  information, delivery updates, and other order details.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Trackingpage;